/**
 * Vertical schema validation schemas.
 * Defines the structure for vertical-specific attribute schemas.
 * Each vertical has a schema defining user_inputs and product_attributes.
 */
import { z } from "zod";
import { AttributeDefinitionSchema, DerivedAttributeDefinitionSchema } from "./attribute";
// ============================================================================
// Vertical Schema Version
// ============================================================================
/** Schema version metadata */
export const SchemaVersionSchema = z
    .object({
    version: z.number().int().min(1),
    createdAt: z.string().datetime(),
    createdBy: z.string().min(1),
    changeDescription: z.string().max(500).optional(),
    breaking: z.boolean().default(false),
})
    .strict();
/** Schema change record */
export const SchemaChangeSchema = z
    .object({
    type: z.enum(["add", "remove", "modify"]),
    attribute: z.string().min(1),
    description: z.string().max(500),
    breaking: z.boolean(),
    previousDefinition: z.unknown().optional(),
    newDefinition: z.unknown().optional(),
})
    .strict();
// ============================================================================
// Vertical Schema Definition
// ============================================================================
/**
 * Complete vertical schema definition.
 * Defines user_inputs and product_attributes for a vertical.
 * This is the authoritative schema stored in R2.
 */
export const VerticalSchemaSchema = z
    .object({
    verticalKey: z
        .string()
        .min(1)
        .regex(/^[a-z][a-z0-9-]*$/, "Vertical key must be kebab-case"),
    version: z.number().int().min(1),
    displayName: z.string().min(1).max(100).optional(),
    description: z.string().max(1000).optional(),
    /** User input attributes (what consumers provide for comparison) */
    userInputs: z.array(AttributeDefinitionSchema).min(1),
    /** Product attributes (what products contain) */
    productAttributes: z.array(AttributeDefinitionSchema).min(1),
    /** Derived attributes computed from other attributes */
    derivedAttributes: z.array(DerivedAttributeDefinitionSchema).optional(),
    /** Metadata */
    metadata: z
        .object({
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
        createdBy: z.string().optional(),
        updatedBy: z.string().optional(),
    })
        .strict()
        .optional(),
})
    .strict()
    .refine((data) => {
    // Validate unique attribute keys within each section
    const userInputKeys = new Set(data.userInputs.map((a) => a.key));
    if (userInputKeys.size !== data.userInputs.length) {
        return false;
    }
    const productAttrKeys = new Set(data.productAttributes.map((a) => a.key));
    if (productAttrKeys.size !== data.productAttributes.length) {
        return false;
    }
    // Derived attributes must have unique keys and not conflict with product attributes
    if (data.derivedAttributes) {
        const derivedKeys = new Set(data.derivedAttributes.map((a) => a.key));
        if (derivedKeys.size !== data.derivedAttributes.length) {
            return false;
        }
        // Check for conflicts with product attributes
        for (const key of derivedKeys) {
            if (productAttrKeys.has(key)) {
                return false;
            }
        }
    }
    return true;
}, {
    message: "Attribute keys must be unique within each section and derived attributes must not conflict with product attributes",
})
    .refine((data) => {
    // Validate that derived attribute dependencies reference valid product attributes
    if (!data.derivedAttributes)
        return true;
    const productAttrKeys = new Set(data.productAttributes.map((a) => a.key));
    const derivedKeys = new Set();
    for (const derived of data.derivedAttributes) {
        for (const dep of derived.dependencies) {
            // Dependency must be a product attribute or a previously defined derived attribute
            if (!productAttrKeys.has(dep) && !derivedKeys.has(dep)) {
                return false;
            }
        }
        derivedKeys.add(derived.key);
    }
    return true;
}, {
    message: "Derived attribute dependencies must reference valid product attributes or previously defined derived attributes",
});
// ============================================================================
// Schema Validation Helpers
// ============================================================================
/** Get all attribute keys from a vertical schema */
export function getAllAttributeKeys(schema) {
    const keys = [];
    for (const attr of schema.userInputs) {
        keys.push(`userInputs.${attr.key}`);
    }
    for (const attr of schema.productAttributes) {
        keys.push(`productAttributes.${attr.key}`);
    }
    if (schema.derivedAttributes) {
        for (const attr of schema.derivedAttributes) {
            keys.push(`derivedAttributes.${attr.key}`);
        }
    }
    return keys;
}
/** Find an attribute definition by key */
export function findAttributeDefinition(schema, key, section) {
    if (!section || section === "userInputs") {
        const found = schema.userInputs.find((a) => a.key === key);
        if (found)
            return found;
    }
    if (!section || section === "productAttributes") {
        const found = schema.productAttributes.find((a) => a.key === key);
        if (found)
            return found;
    }
    if (!section || section === "derivedAttributes") {
        const found = schema.derivedAttributes?.find((a) => a.key === key);
        if (found)
            return found;
    }
    return undefined;
}
/** Get required attributes from a section */
export function getRequiredAttributes(schema, section) {
    return schema[section].filter((a) => a.required);
}
/** Get optional attributes from a section */
export function getOptionalAttributes(schema, section) {
    return schema[section].filter((a) => !a.required);
}
/** Check if a schema change is breaking */
export function isBreakingChange(change) {
    // Removing any attribute is breaking
    if (change.type === "remove")
        return true;
    // Adding a required attribute is breaking
    if (change.type === "add" && change.newDefinition) {
        const newDef = change.newDefinition;
        if (newDef.required === true)
            return true;
    }
    // Modifying to be required is breaking
    if (change.type === "modify") {
        const prev = change.previousDefinition;
        const next = change.newDefinition;
        if (prev?.required === false && next?.required === true)
            return true;
        // Changing type is breaking
        const prevType = change.previousDefinition?.type;
        const nextType = change.newDefinition?.type;
        if (prevType !== nextType)
            return true;
    }
    return false;
}
/** Compare two schema versions and identify changes */
export function compareSchemas(oldSchema, newSchema) {
    const changes = [];
    // Compare user inputs
    compareAttributeLists(oldSchema.userInputs, newSchema.userInputs, "userInput", changes);
    // Compare product attributes
    compareAttributeLists(oldSchema.productAttributes, newSchema.productAttributes, "productAttribute", changes);
    // Compare derived attributes
    compareAttributeLists(oldSchema.derivedAttributes ?? [], newSchema.derivedAttributes ?? [], "derivedAttribute", changes);
    const hasBreakingChanges = changes.some(isBreakingChange);
    return {
        hasChanges: changes.length > 0,
        hasBreakingChanges,
        changes,
    };
}
function compareAttributeLists(oldAttrs, newAttrs, prefix, changes) {
    const oldMap = new Map(oldAttrs.map((a) => [a.key, a]));
    const newMap = new Map(newAttrs.map((a) => [a.key, a]));
    // Find removed attributes
    for (const [key, attr] of oldMap) {
        if (!newMap.has(key)) {
            changes.push({
                type: "remove",
                attribute: `${prefix}.${key}`,
                description: `Removed ${prefix} "${key}"`,
                breaking: true,
                previousDefinition: attr,
            });
        }
    }
    // Find added attributes
    for (const [key, attr] of newMap) {
        if (!oldMap.has(key)) {
            changes.push({
                type: "add",
                attribute: `${prefix}.${key}`,
                description: `Added ${prefix} "${key}"`,
                breaking: attr.required,
                newDefinition: attr,
            });
        }
    }
    // Find modified attributes
    for (const [key, newAttr] of newMap) {
        const oldAttr = oldMap.get(key);
        if (oldAttr && JSON.stringify(oldAttr) !== JSON.stringify(newAttr)) {
            const breaking = oldAttr.type !== newAttr.type || (!oldAttr.required && newAttr.required);
            changes.push({
                type: "modify",
                attribute: `${prefix}.${key}`,
                description: `Modified ${prefix} "${key}"`,
                breaking,
                previousDefinition: oldAttr,
                newDefinition: newAttr,
            });
        }
    }
}
